import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface JobPostState {
  title: string;
  level: string;
  tags: string[];
  description: string;
  responsibilities: string[];
  skills: string[];
  payType: string;
  payAmount: string;
  payAmountMax: string;
  jobType: string;
  payRate: string;
  duration: string;
  durationType: string;
  primaryLanguage: string;
  questions: string[];
  workSite: string;
  specialization: string;
  details: string;
  regionalRates: Array<{ region: string; rate: string }>;
  activeStep: number;
  isLoading: boolean;
  editingJobLocator?: string;
  creationMethod?: 'ai' | 'manual';
  deliverables: string[];
}

const initialState: JobPostState = {
  title: '',
  level: 'flexible',
  tags: [],
  description: '',
  responsibilities: [],
  skills: [],
  payType: 'Fixed',
  payAmount: '',
  payAmountMax: '',
  jobType: 'Short-term',
  payRate: 'One-time',
  duration: '',
  durationType: '',
  primaryLanguage: '',
  questions: [],
  workSite: 'remote',
  specialization: '',
  details: '',
  regionalRates: [],
  activeStep: 0,
  isLoading: false,
  editingJobLocator: undefined,
  creationMethod: undefined,
  deliverables: [],
};

export const jobPostCreationSlice = createSlice({
  name: 'jobPostCreation',
  initialState,
  reducers: {
    setTitle: (state, action: PayloadAction<string>) => {
      state.title = action.payload;
    },
    setLevel: (state, action: PayloadAction<string>) => {
      state.level = action.payload;
    },
    setTags: (state, action: PayloadAction<string[]>) => {
      state.tags = action.payload;
    },
    setDescription: (state, action: PayloadAction<string>) => {
      state.description = action.payload;
    },
    setResponsibilities: (state, action: PayloadAction<string[]>) => {
      state.responsibilities = action.payload;
    },
    setSkills: (state, action: PayloadAction<string[]>) => {
      state.skills = action.payload;
    },
    setPayType: (state, action: PayloadAction<string>) => {
      state.payType = action.payload;
    },
    setPayAmount: (state, action: PayloadAction<string>) => {
      state.payAmount = action.payload;
    },
    setPayAmountMax: (state, action: PayloadAction<string>) => {
      state.payAmountMax = action.payload;
    },
    setJobType: (state, action: PayloadAction<string>) => {
      state.jobType = action.payload;
    },
    setPayRate: (state, action: PayloadAction<string>) => {
      state.payRate = action.payload;
    },
    setDuration: (state, action: PayloadAction<string>) => {
      state.duration = action.payload;
    },
    setDurationType: (state, action: PayloadAction<string>) => {
      state.durationType = action.payload;
    },
    setPrimaryLanguage: (state, action: PayloadAction<string>) => {
      state.primaryLanguage = action.payload;
    },
    setQuestions: (state, action: PayloadAction<string[]>) => {
      state.questions = action.payload;
    },
    setWorkSite: (state, action: PayloadAction<string>) => {
      state.workSite = action.payload;
    },
    setSpecialization: (state, action: PayloadAction<string>) => {
      state.specialization = action.payload;
    },
    setDetails: (state, action: PayloadAction<string>) => {
      state.details = action.payload;
    },
    setRegionalRates: (state, action: PayloadAction<Array<{ region: string; rate: string }>>) => {
      state.regionalRates = action.payload;
    },
    setActiveStep: (state, action: PayloadAction<number>) => {
      state.activeStep = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setEditingJobLocator: (state, action: PayloadAction<string | undefined>) => {
      state.editingJobLocator = action.payload;
    },
    setCreationMethod: (state, action: PayloadAction<'ai' | 'manual'>) => {
      state.creationMethod = action.payload;
    },
    setDeliverables: (state, action: PayloadAction<string[]>) => {
      state.deliverables = action.payload;
    },
    resetJobPost: (state) => {
      return initialState;
    },
  },
});

export const {
  setTitle,
  setLevel,
  setTags,
  setDescription,
  setResponsibilities,
  setSkills,
  setPayType,
  setPayAmount,
  setPayAmountMax,
  setJobType,
  setPayRate,
  setDuration,
  setDurationType,
  setPrimaryLanguage,
  setQuestions,
  setWorkSite,
  setSpecialization,
  setDetails,
  setRegionalRates,
  setActiveStep,
  setIsLoading,
  setEditingJobLocator,
  setCreationMethod,
  setDeliverables,
  resetJobPost,
} = jobPostCreationSlice.actions;

export default jobPostCreationSlice.reducer; 